import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  ApiPathEnum,
  HolderBankEndpointEnum,
} from '../constants/endpoints.enum';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import {
  BankAccountsResponse,
  Filters,
  HolderAccountResponse,
} from '../../pages/accounting/banking/interface/bank-accounts.interface';
import { ApiResponse } from '../interfaces/api-response.inteface';
import { HOLDERS_CACHE } from '../constants/constants';

@Injectable({
  providedIn: 'root',
})
export class HolderAccountBankService {
  public API_URL = `${environment.API_URL}${ApiPathEnum.HolderAccountHolder}`;
  private readonly holdersCacheTtlMs = HOLDERS_CACHE;
  private readonly holdersCache = new Map<
    string,
    { expiresAt: number; data: HolderAccountResponse }
  >();
  private readonly holdersInFlight = new Map<
    string,
    Observable<HolderAccountResponse>
  >();

  constructor(private http: HttpClient) {}

  getAllHoldersAccountData(
    page: number,
    limit: number,
    countryId: number,
    bankId: number,
    searchTerm?: string,
  ): Observable<HolderAccountResponse> {
    const normalizedSearch = (searchTerm || '').trim();
    const cacheKey = [
      page,
      limit,
      countryId || 0,
      bankId || 0,
      normalizedSearch,
    ].join('|');
    const now = Date.now();
    const cached = this.holdersCache.get(cacheKey);
    if (cached && cached.expiresAt > now) {
      return of(cached.data);
    }

    const inFlight = this.holdersInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    if (normalizedSearch) {
      params = params.set('searchTerm', normalizedSearch);
    }
    if (bankId) {
      params = params.set('bankId', bankId);
    }
    if (countryId) {
      params = params.set('countryId', countryId);
    }
    const request$ = this.http
      .get<HolderAccountResponse>(
        `${this.API_URL}${HolderBankEndpointEnum.GetHoldersAccountBankData}`,
        { params },
      )
      .pipe(
        tap((data) => {
          this.holdersCache.set(cacheKey, {
            data,
            expiresAt: Date.now() + this.holdersCacheTtlMs,
          });
        }),
        finalize(() => {
          this.holdersInFlight.delete(cacheKey);
        }),
        shareReplay(1),
      );

    this.holdersInFlight.set(cacheKey, request$);
    return request$;
  }
  public getAllBankAccounts(
    page: number,
    limit: number,
    filters?: Filters,
  ): Observable<ApiResponse<BankAccountsResponse>> {
    const url = `${this.API_URL}${HolderBankEndpointEnum.GetAllBankAccounts}`;
    const params = { page: page.toString(), limit: limit.toString() };
    return this.http.post<ApiResponse<BankAccountsResponse>>(url, {
      params,
      filters,
    });
  }

  updateSatusBankAccounts(data: {
    bankHolderId: number;
    active: number;
  }): Observable<ApiResponse<BankAccountsResponse>> {
    return this.http.put<ApiResponse<BankAccountsResponse>>(
      `${this.API_URL}/update-bank-account-active`,
      data,
    );
  }
  createBankAccounts(
    formData: FormData,
  ): Observable<ApiResponse<BankAccountsResponse>> {
    return this.http.post<ApiResponse<BankAccountsResponse>>(
      `${this.API_URL}/create-bank-account`,
      formData,
    );
  }

  updateBankAccounts(
    formData: FormData,
  ): Observable<ApiResponse<BankAccountsResponse>> {
    return this.http.patch<ApiResponse<BankAccountsResponse>>(
      `${this.API_URL}/update-bank-account`,
      formData,
    );
  }

  verifyBankAccountEditAccess(data: {
    bankHolderId: number;
    method: 'password' | 'pin';
    value: string;
  }): Observable<ApiResponse<{ editAuthToken: string }>> {
    return this.http.post<ApiResponse<{ editAuthToken: string }>>(
      `${this.API_URL}${HolderBankEndpointEnum.VerifyBankAccountEditAccess}`,
      data,
    );
  }
}
